/**
 * Doris & Co - B2G1 Roll-On Offer Cart Synchronization & UI Manager
 *
 * Rules:
 *  - Eligible products (any combination of 2 paid units):
 *      1. doris-co-sunveil-primer-sunscreen-spf-50
 *      2. doris-co-radiance-c-serum
 *      3. fresh-confidence-all-day
 *  - Free Gift: Exactly 1 unit of Fresh Confidence All Day Roll-On (fresh-confidence-all-day)
 *  - Gift Line Identifier: line item property `_doris_free_gift: "true"`
 *  - Paid Roll-Ons (without `_doris_free_gift: "true"`) are NEVER removed or modified.
 *  - Free gift lines are NEVER counted towards qualifying units.
 *  - When eligible paid quantity < 2: remove any marked free gift in REAL TIME.
 *  - When eligible paid quantity >= 2: ensure exactly one marked free gift exists in REAL TIME.
 *  - Real-Time / Optimistic UI:
 *      * Immediate feedback on add/remove (0ms visual delay).
 *      * Pre-injection into cart-drawer sections so free gift is present when drawer opens.
 *      * Background reconciliation with Shopify Cart API.
 */

(function () {
  'use strict';

  if (window.DorisB2G1Loaded) {
    return;
  }
  window.DorisB2G1Loaded = true;

  const ELIGIBLE_HANDLES = [
    'doris-co-sunveil-primer-sunscreen-spf-50',
    'doris-co-radiance-c-serum',
    'fresh-confidence-all-day'
  ];

  const GIFT_HANDLE = 'fresh-confidence-all-day';
  const GIFT_VARIANT_FALLBACK = '41620473675909';
  const GIFT_PROP_KEY = '_doris_free_gift';
  const GIFT_PROP_VAL = 'true';

  let cachedGiftVariantId = GIFT_VARIANT_FALLBACK;
  let isSyncing = false;
  let pendingSync = false;
  let debounceTimer = null;
  let mutationCountInWindow = 0;
  let windowResetTimer = null;
  const MAX_MUTATIONS_PER_WINDOW = 12;

  function getRoutes() {
    const rawRoot = (window.routes && window.routes.root_url) || '/';
    const rootUrl = rawRoot.endsWith('/') ? rawRoot : rawRoot + '/';
    return {
      rootUrl,
      cartUrl: `${rootUrl}cart.js`,
      cartAddUrl: `${rootUrl}cart/add.js`,
      cartUpdateUrl: `${rootUrl}cart/update.js`,
      cartChangeUrl: `${rootUrl}cart/change.js`
    };
  }

  /**
   * Safely extract product handle from a line item object
   */
  function getItemHandle(item) {
    if (!item) return '';
    if (typeof item.handle === 'string' && item.handle.length > 0) {
      return item.handle;
    }
    if (typeof item.product_handle === 'string' && item.product_handle.length > 0) {
      return item.product_handle;
    }
    if (typeof item.url === 'string') {
      const match = item.url.match(/\/products\/([a-zA-Z0-9\-_]+)/);
      if (match) return match[1];
    }
    return '';
  }

  /**
   * Check whether a line item is marked as the Doris B2G1 free gift
   */
  function isGiftItem(item) {
    if (!item || !item.properties) return false;
    const props = item.properties;

    if (typeof props === 'object' && !Array.isArray(props)) {
      const val = props[GIFT_PROP_KEY];
      if (val === GIFT_PROP_VAL || val === true || String(val).toLowerCase() === 'true') {
        return true;
      }
    }

    if (Array.isArray(props)) {
      return props.some((p) => {
        if (!p) return false;
        if (Array.isArray(p)) {
          return p[0] === GIFT_PROP_KEY && (p[1] === GIFT_PROP_VAL || p[1] === true || String(p[1]).toLowerCase() === 'true');
        }
        if (typeof p === 'object') {
          const key = p.name || p.key || (p[GIFT_PROP_KEY] !== undefined ? GIFT_PROP_KEY : null);
          const val = p.value !== undefined ? p.value : p[GIFT_PROP_KEY];
          return key === GIFT_PROP_KEY && (val === GIFT_PROP_VAL || val === true || String(val).toLowerCase() === 'true');
        }
        return false;
      });
    }

    return false;
  }

  /**
   * Resolve variant ID for the free Roll-On dynamically
   */
  async function resolveGiftVariantId() {
    if (cachedGiftVariantId) return cachedGiftVariantId;

    const wrapper = document.getElementById('cart-b2g1-progress-wrapper');
    if (wrapper && wrapper.dataset.giftVariantId && wrapper.dataset.giftVariantId.trim().length > 0) {
      cachedGiftVariantId = String(wrapper.dataset.giftVariantId).trim();
      return cachedGiftVariantId;
    }

    return GIFT_VARIANT_FALLBACK;
  }

  /**
   * Analyze the cart to count eligible paid units and identify marked free gift lines
   */
  function analyzeCart(cart) {
    if (!cart || !Array.isArray(cart.items)) {
      return {
        qualifyingPaidQty: 0,
        giftItems: [],
        totalGiftQty: 0,
        qualifies: false
      };
    }

    let qualifyingPaidQty = 0;
    const giftItems = [];
    let totalGiftQty = 0;

    cart.items.forEach((item) => {
      const isGift = isGiftItem(item);
      const qty = Number(item.quantity) || 0;

      if (isGift) {
        giftItems.push(item);
        totalGiftQty += qty;
      } else {
        const handle = getItemHandle(item);
        if (ELIGIBLE_HANDLES.includes(handle)) {
          qualifyingPaidQty += qty;
        }
      }
    });

    return {
      qualifyingPaidQty,
      giftItems,
      totalGiftQty,
      qualifies: qualifyingPaidQty >= 2
    };
  }

  /**
   * Optimistically insert the free gift line into the cart drawer DOM in real time (0ms)
   */
  function optimisticAddGift() {
    if (document.querySelector('[data-b2g1-gift="true"], .cart-item--b2g1-gift')) {
      return;
    }

    const tbody = document.querySelector('#CartDrawer tbody') || document.querySelector('cart-drawer tbody');
    const template = document.getElementById('cart-b2g1-gift-row-template');
    if (tbody && template) {
      const clone = template.content.cloneNode(true);
      tbody.prepend(clone);
    }
  }

  /**
   * Optimistically remove any free gift lines from the cart drawer DOM in real time (0ms)
   */
  function optimisticRemoveGift() {
    const giftRows = document.querySelectorAll('[data-b2g1-gift="true"], .cart-item--b2g1-gift');
    giftRows.forEach((row) => {
      row.remove();
    });
  }

  /**
   * Pre-inject gift markup into section response before CartDrawer.renderContents() runs
   * Ensures the gift is ALREADY visible the instant the drawer slides open!
   */
  function injectGiftIntoSections(sections) {
    if (!sections || typeof sections['cart-drawer'] !== 'string') return;

    try {
      const doc = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
      const tbody = doc.querySelector('#CartDrawer tbody') || doc.querySelector('tbody');
      const template = document.getElementById('cart-b2g1-gift-row-template');
      if (tbody && template) {
        tbody.prepend(template.content.cloneNode(true));
      }

      const wrapper = doc.querySelector('#cart-b2g1-progress-wrapper');
      if (wrapper) {
        wrapper.dataset.hasGift = 'true';
        wrapper.classList.remove('cart-b2g1--initial', 'cart-b2g1--partial');
        wrapper.classList.add('cart-b2g1--unlocked');

        const bannerMsg = doc.querySelector('#cart-b2g1-banner-message');
        if (bannerMsg) {
          bannerMsg.innerHTML = "<strong>Offer Unlocked!</strong> You've earned a <strong>FREE Roll-On</strong>!";
        }
        const trackFill = doc.querySelector('#cart-b2g1-track-fill');
        if (trackFill) {
          trackFill.style.width = '100%';
        }
        const step2 = doc.querySelector('#cart-b2g1-step-2');
        const step2Sub = doc.querySelector('#cart-b2g1-step-2-sub');
        if (step2) {
          step2.classList.remove('is-pending', 'is-active');
          step2.classList.add('is-completed');
          if (step2Sub) step2Sub.textContent = '2/2 Added';
        }
        const step3 = doc.querySelector('#cart-b2g1-step-3');
        const step3Sub = doc.querySelector('#cart-b2g1-step-3-sub');
        if (step3) {
          step3.classList.remove('is-locked');
          step3.classList.add('is-completed', 'is-gift-unlocked');
          if (step3Sub) step3Sub.textContent = 'Unlocked!';
        }
      }

      sections['cart-drawer'] = doc.body.innerHTML;
    } catch (e) {
      console.warn('[Doris B2G1] Failed to pre-inject gift into sections:', e);
    }
  }

  /**
   * Pre-strip gift markup from section response when cart no longer qualifies
   */
  function removeGiftFromSections(sections) {
    if (!sections || typeof sections['cart-drawer'] !== 'string') return;

    try {
      const doc = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
      doc.querySelectorAll('[data-b2g1-gift="true"], .cart-item--b2g1-gift').forEach((el) => el.remove());
      sections['cart-drawer'] = doc.body.innerHTML;
    } catch (e) {
      console.warn('[Doris B2G1] Failed to strip gift from sections:', e);
    }
  }

  /**
   * Safely update section HTML in the DOM from Shopify section rendering response
   */
  function updateSectionsFromResponse(sections) {
    if (!sections) return;

    if (sections['cart-drawer']) {
      const parsed = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
      const newCartDrawer = parsed.querySelector('#CartDrawer');
      const currentCartDrawer = document.querySelector('#CartDrawer');

      if (newCartDrawer && currentCartDrawer) {
        currentCartDrawer.innerHTML = newCartDrawer.innerHTML;
      }

      const drawerWrapper = document.querySelector('cart-drawer');
      if (drawerWrapper) {
        const hasEmpty = parsed.querySelector('.drawer__inner-empty') !== null;
        drawerWrapper.classList.toggle('is-empty', hasEmpty);

        const overlay = drawerWrapper.querySelector('#CartDrawer-Overlay');
        if (overlay) {
          overlay.addEventListener('click', () => drawerWrapper.close());
        }
      }
    }

    if (sections['cart-icon-bubble']) {
      const bubbleEl = document.querySelector('#cart-icon-bubble');
      if (bubbleEl) {
        const parsedBubble = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html');
        const content = parsedBubble.querySelector('.shopify-section') || parsedBubble.body;
        bubbleEl.innerHTML = content.innerHTML;
      }
    }
  }

  /**
   * Dynamically update progress bar UI elements in DOM in real time
   */
  function updateProgressUI(qualifyingCount, hasGift) {
    const wrapper = document.getElementById('cart-b2g1-progress-wrapper');
    if (!wrapper) return;

    wrapper.dataset.qualifyingCount = String(qualifyingCount);
    wrapper.dataset.hasGift = hasGift ? 'true' : 'false';

    wrapper.classList.remove('cart-b2g1--initial', 'cart-b2g1--partial', 'cart-b2g1--unlocked');
    if (qualifyingCount >= 2) {
      wrapper.classList.add('cart-b2g1--unlocked');
    } else if (qualifyingCount === 1) {
      wrapper.classList.add('cart-b2g1--partial');
    } else {
      wrapper.classList.add('cart-b2g1--initial');
    }

    // Update banner message
    const messageEl = document.getElementById('cart-b2g1-banner-message');
    if (messageEl) {
      if (qualifyingCount === 0) {
        messageEl.innerHTML = 'Add <strong>2 eligible items</strong> to unlock a <strong>FREE Roll-On!</strong>';
      } else if (qualifyingCount === 1) {
        messageEl.innerHTML = 'Add <strong>1 more eligible item</strong> to unlock a <strong>FREE Roll-On!</strong>';
      } else {
        messageEl.innerHTML = "<strong>Offer Unlocked!</strong> You've earned a <strong>FREE Roll-On</strong>!";
      }
    }

    // Update track fill
    const trackFill = document.getElementById('cart-b2g1-track-fill');
    if (trackFill) {
      let pct = 15;
      if (qualifyingCount === 1) pct = 50;
      else if (qualifyingCount >= 2) pct = 100;
      trackFill.style.width = `${pct}%`;
    }

    // Step 2
    const step2 = document.getElementById('cart-b2g1-step-2');
    const step2Sub = document.getElementById('cart-b2g1-step-2-sub');
    if (step2) {
      step2.classList.remove('is-pending', 'is-active', 'is-completed');
      if (qualifyingCount >= 2) {
        step2.classList.add('is-completed');
        if (step2Sub) step2Sub.textContent = '2/2 Added';
      } else if (qualifyingCount === 1) {
        step2.classList.add('is-active');
        if (step2Sub) step2Sub.textContent = '1/2 Added';
      } else {
        step2.classList.add('is-pending');
        if (step2Sub) step2Sub.textContent = '0/2 Added';
      }
    }

    // Step 3
    const step3 = document.getElementById('cart-b2g1-step-3');
    const step3Sub = document.getElementById('cart-b2g1-step-3-sub');
    if (step3) {
      step3.classList.remove('is-locked', 'is-completed', 'is-gift-unlocked');
      if (qualifyingCount >= 2) {
        step3.classList.add('is-completed', 'is-gift-unlocked');
        if (step3Sub) step3Sub.textContent = 'Unlocked!';
      } else {
        step3.classList.add('is-locked');
        if (step3Sub) step3Sub.textContent = 'Free Gift';
      }
    }
  }

  /**
   * Main synchronization routine
   */
  async function performGiftSync(providedCart = null) {
    if (isSyncing) {
      pendingSync = true;
      return;
    }

    if (mutationCountInWindow >= MAX_MUTATIONS_PER_WINDOW) {
      console.warn('[Doris B2G1] Circuit breaker: max mutations reached in active window.');
      return;
    }

    isSyncing = true;
    try {
      const routes = getRoutes();
      let cart = providedCart;

      // Use providedCart directly if it contains items array (avoids redundant network roundtrip!)
      if (!cart || !Array.isArray(cart.items)) {
        const fetchRes = await fetch(`${routes.cartUrl}?t=${Date.now()}`, {
          headers: { Accept: 'application/json' },
          cache: 'no-store'
        });
        if (!fetchRes.ok) {
          throw new Error(`Failed to fetch ${routes.cartUrl}: ${fetchRes.status}`);
        }
        cart = await fetchRes.json();
      }

      const state = analyzeCart(cart);

      // Keep progress UI synced immediately
      updateProgressUI(state.qualifyingPaidQty, state.totalGiftQty > 0);

      let actionTaken = false;

      // Case 1: Cart qualifies, but no gift in cart -> ADD EXACTLY 1 GIFT
      if (state.qualifies && state.totalGiftQty === 0) {
        actionTaken = true;
        recordMutation();

        // Optimistically render gift in DOM immediately (0ms)!
        optimisticAddGift();
        updateProgressUI(state.qualifyingPaidQty, true);

        const giftVariantId = await resolveGiftVariantId();
        const addPayload = {
          items: [
            {
              id: Number(giftVariantId),
              quantity: 1,
              properties: {
                [GIFT_PROP_KEY]: GIFT_PROP_VAL
              }
            }
          ],
          sections: 'cart-drawer,cart-icon-bubble'
        };

        const addRes = await fetch(routes.cartAddUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify(addPayload)
        });

        if (addRes.ok) {
          const result = await addRes.json();
          updateSectionsFromResponse(result.sections);
          updateProgressUI(state.qualifyingPaidQty, true);
        } else {
          console.error('[Doris B2G1] Add gift request failed:', await addRes.text());
        }
      }
      // Case 2: Cart does NOT qualify, but gift is present -> REMOVE ALL GIFTS
      else if (!state.qualifies && state.totalGiftQty > 0) {
        actionTaken = true;
        recordMutation();

        // Optimistically remove gift from DOM immediately (0ms)!
        optimisticRemoveGift();
        updateProgressUI(state.qualifyingPaidQty, false);

        const updates = {};
        state.giftItems.forEach((giftItem) => {
          const keyOrId = giftItem.key || giftItem.id;
          updates[keyOrId] = 0;
        });

        const updateRes = await fetch(routes.cartUpdateUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({
            updates,
            sections: 'cart-drawer,cart-icon-bubble'
          })
        });

        if (updateRes.ok) {
          const result = await updateRes.json();
          updateSectionsFromResponse(result.sections);
          updateProgressUI(state.qualifyingPaidQty, false);
        } else {
          console.error('[Doris B2G1] Remove gift request failed:', await updateRes.text());
        }
      }
      // Case 3: Duplicate gifts
      else if (state.qualifies && (state.totalGiftQty > 1 || state.giftItems.length > 1)) {
        actionTaken = true;
        recordMutation();

        const updates = {};
        const primaryGiftKey = state.giftItems[0].key || state.giftItems[0].id;
        updates[primaryGiftKey] = 1;
        for (let i = 1; i < state.giftItems.length; i++) {
          const extraKey = state.giftItems[i].key || state.giftItems[i].id;
          updates[extraKey] = 0;
        }

        const updateRes = await fetch(routes.cartUpdateUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({
            updates,
            sections: 'cart-drawer,cart-icon-bubble'
          })
        });

        if (updateRes.ok) {
          const result = await updateRes.json();
          updateSectionsFromResponse(result.sections);
          updateProgressUI(state.qualifyingPaidQty, true);
        }
      }

      if (!actionTaken) {
        updateProgressUI(state.qualifyingPaidQty, state.totalGiftQty > 0);
      }
    } catch (err) {
      console.error('[Doris B2G1] Error during sync:', err);
    } finally {
      isSyncing = false;
      if (pendingSync) {
        pendingSync = false;
        scheduleSync(50);
      }
    }
  }

  function recordMutation() {
    mutationCountInWindow++;
    if (!windowResetTimer) {
      windowResetTimer = setTimeout(() => {
        mutationCountInWindow = 0;
        windowResetTimer = null;
      }, 6000);
    }
  }

  function scheduleSync(delay = 0, providedCart = null) {
    if (debounceTimer) clearTimeout(debounceTimer);
    if (delay <= 0) {
      performGiftSync(providedCart);
    } else {
      debounceTimer = setTimeout(() => {
        performGiftSync(providedCart);
      }, delay);
    }
  }

  // --- Real-Time Capture Phase Click Handler for Minus, Plus, and Remove ---
  document.addEventListener(
    'click',
    (event) => {
      // 1. Minus button or Remove button clicked on an item row
      const minusBtn = event.target.closest('.quantity__button[name="minus"]');
      const removeBtn = event.target.closest('cart-remove-button');
      if (minusBtn || removeBtn) {
        const itemRow = (minusBtn || removeBtn).closest('.cart-item');
        if (!itemRow || itemRow.dataset.b2g1Gift === 'true') return;

        const handle = itemRow.dataset.productHandle || '';
        if (ELIGIBLE_HANDLES.includes(handle)) {
          const wrapper = document.getElementById('cart-b2g1-progress-wrapper');
          const currentCount = wrapper ? parseInt(wrapper.dataset.qualifyingCount || '0', 10) : 0;
          const qtyInput = itemRow.querySelector('.quantity__input');
          const currentQty = qtyInput ? parseInt(qtyInput.value || '1', 10) : 1;
          const delta = minusBtn ? 1 : currentQty;
          const newCount = Math.max(0, currentCount - delta);

          if (newCount < 2) {
            // Instantly remove gift from UI in 0ms!
            optimisticRemoveGift();
            updateProgressUI(newCount, false);
          } else {
            updateProgressUI(newCount, true);
          }
        }
      }

      // 2. Plus button clicked on an item row
      const plusBtn = event.target.closest('.quantity__button[name="plus"]');
      if (plusBtn) {
        const itemRow = plusBtn.closest('.cart-item');
        if (!itemRow || itemRow.dataset.b2g1Gift === 'true') return;

        const handle = itemRow.dataset.productHandle || '';
        if (ELIGIBLE_HANDLES.includes(handle)) {
          const wrapper = document.getElementById('cart-b2g1-progress-wrapper');
          const currentCount = wrapper ? parseInt(wrapper.dataset.qualifyingCount || '0', 10) : 0;
          const newCount = currentCount + 1;

          if (newCount >= 2) {
            // Instantly add gift to UI in 0ms!
            optimisticAddGift();
            updateProgressUI(newCount, true);
          } else {
            updateProgressUI(newCount, false);
          }
        }
      }
    },
    true
  );

  // --- Event Bindings ---

  // 1. PubSub cartUpdate event: triggered on external add-to-cart or cart modifications
  if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS) {
    subscribe(window.PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event && event.source === 'cart-b2g1') {
        return; // Ignore own published events
      }

      // If cartData came with sections['cart-drawer'] (e.g. from product-form when adding 2nd item):
      if (event && event.cartData && event.cartData.sections && event.cartData.sections['cart-drawer']) {
        const parsed = new DOMParser().parseFromString(event.cartData.sections['cart-drawer'], 'text/html');
        const wrapper = parsed.querySelector('#cart-b2g1-progress-wrapper');
        if (wrapper) {
          const qCount = parseInt(wrapper.dataset.qualifyingCount || '0', 10);
          const hasGift = wrapper.dataset.hasGift === 'true';

          if (qCount >= 2 && !hasGift) {
            // Inject gift into sections BEFORE drawer renders!
            injectGiftIntoSections(event.cartData.sections);
            optimisticAddGift();
            updateProgressUI(qCount, true);
          } else if (qCount < 2 && hasGift) {
            removeGiftFromSections(event.cartData.sections);
            optimisticRemoveGift();
            updateProgressUI(qCount, false);
          }
        }
      }

      // If cartData contains complete items array (e.g. from /cart/change.js in cart.js):
      if (event && event.cartData && Array.isArray(event.cartData.items)) {
        const state = analyzeCart(event.cartData);
        if (state.qualifies && state.totalGiftQty === 0) {
          optimisticAddGift();
          updateProgressUI(state.qualifyingPaidQty, true);
        } else if (!state.qualifies && state.totalGiftQty > 0) {
          optimisticRemoveGift();
          updateProgressUI(state.qualifyingPaidQty, false);
        }
        scheduleSync(0, event.cartData);
        return;
      }

      scheduleSync(0);
    });
  }

  // 2. Custom DOM event cartUpdated
  document.addEventListener('cartUpdated', () => {
    scheduleSync(0);
  });

  // 3. Monitor Cart Drawer open state
  function attachDrawerObserver() {
    const drawer = document.querySelector('cart-drawer');
    if (drawer && !drawer.dataset.b2g1ObserverAttached) {
      drawer.dataset.b2g1ObserverAttached = 'true';
      const observer = new MutationObserver((mutations) => {
        for (const m of mutations) {
          if (m.type === 'attributes' && m.attributeName === 'class') {
            if (drawer.classList.contains('active')) {
              scheduleSync(0);
            }
          }
        }
      });
      observer.observe(drawer, { attributes: true });
    }
  }

  // 4. Cart bubble / drawer toggle clicks
  document.addEventListener('click', (event) => {
    if (event.target.closest('#cart-icon-bubble') || event.target.closest('.drawer__close')) {
      scheduleSync(0);
    }
  });

  // 5. Initial boot check
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      attachDrawerObserver();
      scheduleSync(0);
    });
  } else {
    attachDrawerObserver();
    scheduleSync(0);
  }

  // Expose API for external scripts & testing
  window.DorisB2G1 = {
    sync: () => performGiftSync(),
    analyze: analyzeCart,
    resolveGiftVariantId,
    optimisticAdd: optimisticAddGift,
    optimisticRemove: optimisticRemoveGift
  };
})();
